<template>
    <div>
        <div class="grid grid-cols-12 gap-6">
            <div class="hidden md:col-span-1 md:block" />
            <div class="col-span-12 text-left md:col-span-10">
                <h2>Původy názvů a jejich četnost</h2>
                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/puvody_nazvu_a_jejich_cetnost.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Některé městské části připojují k uličním tabulkám i
                        vysvětlení původu názvu. / Zdroj: Marek Lašťovka</span
                    >
                </div>
                <p class="mb-5 font-bold">
                    Proč se pražské ulice jmenují právě tak, jak se jmenují?
                    Článek odhaluje nejčastější typy názvů – od těch podle
                    polohy, osobností či měst až po zapomenuté pomístní názvy –
                    a přináší zajímavosti o tom, kdo nebo co se do pražské mapy
                    zapsalo nejčastěji.
                </p>

                <p class="mb-5">
                    Existuje několik základních typů názvů pražských veřejných
                    prostranství:
                </p>

                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/puvody_nazvu_a_jejich_cetnost_graf.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Podíl jednotlivých typů uličních názvů. / Zdroj: Marek
                        Lašťovka</span
                    >
                </div>

                <p class="font-bold">Názvy podle polohy či místa</p>

                <p class="mb-5">
                    Patří sem pojmenování ulic podle směru či polohy (většina
                    ulic s předložkou, např.
                    <span class="italic"
                        >K zahrádkám, Na Babě, Za Strahovem, Pod křížkem</span
                    >), podle objektů, které se v ní, resp. u ní nacházejí
                    (podle domů, usedlostí či významných objektů např.
                    <span class="italic">
                        Apolinářská, Na Šafránce, Hybernská, Chladírenská</span
                    >) a podle polohy ve čtvrti (nejtypičtější jsou spořilovské
                    ulice např.
                    <span class="italic"
                        >Jižní, Jihozápadní, Severní, Severovýchodní,</span
                    >
                    které jsou opatřeny římskými číslicemi). Tato jména tvoří
                    největší část pražského názvosloví, a to plných 30 % všech
                    názvů.
                </p>

                <p class="font-bold">Názvy podle osob</p>
                <p class="mb-5">
                    Tato skupina názvů je druhá nejčastější mezi uličními názvy
                    a tvoří 29 % z celkového počtu. Jedná se o pojmenování podle
                    historicky doložených osob z nejrůznějších období.
                    Zajímavostí je, že v posledních 20 letech pojmenování podle
                    osob výrazně přibylo, přičemž do této skupiny nebyly
                    zahrnuty názvy podle skupin osob, které tvoří velmi
                    různorodá seskupení od brigádníků, přes cisterciáky, kozáky,
                    až po jugoslávské partyzány, dukelské hrdiny aj., ani
                    postavy z románů, kronik či bájné postavy nejrůznějšího
                    druhu (<span class="italic"
                        >Horymírova, Perunova, Libušina, Aksamitova,
                        Radúzova,</span
                    >
                    ale i
                    <span class="italic">U nás</span>). Obě tyto podskupiny tedy
                    posilují nehomogenní skupinu „ostatních“.
                </p>

                <p class="mb-5">
                    Kupodivu nedávné období budování socialismu nezanechalo tak
                    výraznou stopu v názvech ulic, a to ani v názvech již
                    odstraněných. Marx, Engels, Lenin, Stalin ani Gottwald v
                    pomyslném „top 20“ nejužívanějších názvů v historii
                    nefigurují. Nejčastěji se vyskytují „obrozenecké“ názvy.
                </p>

                <table class="mb-5">
                    <tbody>
                        <tr>
                            <td class="min-w-[300px]">Jan Hus</td>
                            <td>39</td>
                        </tr>
                        <tr>
                            <td>Karel Havlíček Borovský</td>
                            <td>39</td>
                        </tr>
                        <tr>
                            <td>Jan Žižka</td>
                            <td>38</td>
                        </tr>
                        <tr>
                            <td>Jan Amos Komenský</td>
                            <td>32</td>
                        </tr>
                        <tr>
                            <td>Bedřich Smetana</td>
                            <td>32</td>
                        </tr>
                        <tr>
                            <td>František Palacký</td>
                            <td>27</td>
                        </tr>
                        <tr>
                            <td>Miroslav Tyrš</td>
                            <td>26</td>
                        </tr>
                        <tr>
                            <td>Alois Jirásek</td>
                            <td>25</td>
                        </tr>
                        <tr>
                            <td>Jan Neruda</td>
                            <td>24</td>
                        </tr>
                        <tr>
                            <td>Svatopluk Čech</td>
                            <td>22</td>
                        </tr>
                        <tr>
                            <td>František Ladislav Rieger</td>
                            <td>22</td>
                        </tr>
                        <tr>
                            <td>Božena Němcová</td>
                            <td>20</td>
                        </tr>
                        <tr>
                            <td>Vítězslav Hálek</td>
                            <td>20</td>
                        </tr>
                        <tr>
                            <td>Antonín Dvořák</td>
                            <td>18</td>
                        </tr>
                        <tr>
                            <td>Josef Jungmann</td>
                            <td>18</td>
                        </tr>
                        <tr>
                            <td>Václav Beneš Třebízský</td>
                            <td>16</td>
                        </tr>
                        <tr>
                            <td>Jaroslav Vrchlický</td>
                            <td>16</td>
                        </tr>
                        <tr>
                            <td>Karel Jaromír Erben</td>
                            <td>14</td>
                        </tr>
                        <tr>
                            <td>Libuše</td>
                            <td>10</td>
                        </tr>
                        <tr>
                            <td>Karolina Světlá</td>
                            <td>7</td>
                        </tr>
                    </tbody>
                </table>

                <p class="font-bold">Názvy podle obcí v Čechách</p>
                <p class="mb-5">
                    Třetí největší skupinu tvoří názvy podle obcí v Čechách (vsí
                    i měst). Tyto názvy byly původně odvozovány od směru cest do
                    sousedních obcí (<span class="italic"
                        >Dolnoměcholupská
                    </span>

                    vedla z Hostivaře do Dolních Měcholup), v případě hlavních
                    silnic či výpadovek z Prahy i dále (<span class="italic"
                        >Slánská, Českobrodská</span
                    >). Časem však byla užívána v názvech ulic i jména obcí
                    vzdálenějších a byla vesměs dodržována snaha o udržení směru
                    od Prahy (např. ve Strašnicích a Hostivaři jsou vsi východně
                    od Prahy, na Praze 5 západně od Prahy, v Krči jižně od Prahy
                    apod.). V některých částech Prahy jsou vytvořeny systémy
                    uličních názvů podle vsí z jediného okresu (např. Horní
                    Počernice z okresu Rychnov nad Kněžnou). Skupina názvů podle
                    obcí představuje dalších 20 % všech názvů.
                </p>

                <p class="font-bold">Pomístní jména</p>
                <p class="mb-5">
                    Poslední významnou skupinou názvů jsou pomístní jména.
                    Většina z nich byla původně určena vztahem k objektům, které
                    však dávno zmizely, tudíž důvody jejich pojmenování pominuly
                    a mnohdy již nejsme schopni přesně říci, proč tato jména
                    ulice nesou. Kromě nejčastějších názvů typu
                    <span class="italic">K jelenám, Na Křtině </span>
                    apod. sem byla zařazena jména podle řemeslníků, kteří ulici
                    obývali (<span class="italic">Celetná, Truhlářská</span>),
                    či původních určení prostranství (<span class="italic"
                        >Ječná</span
                    >). Celkový podíl takových názvů představuje 10 %.
                </p>

                <p class="font-bold">Zeměpisné názvy</p>
                <p class="mb-5">
                    Zeměpisné názvy (jiné, než podle názvů českých obcí) jsou
                    vzhledem k menšímu počtu sloučeny do jedné skupiny. Tvoří je
                    pojmenování podle států (<span class="italic"
                        >Ruská, Libyjská</span
                    >), zahraničních měst (<span class="italic"
                        >Pařížská, Římská</span
                    >), českých i zahraničních hor (<span class="italic"
                        >Karpatská, Středohorská, Uralská</span
                    >), řek (<span class="italic">Úhlavská, Donská</span>) a
                    rybníků (<span class="italic">Světská </span>
                    aj.). Tato skupina tvoří 4 % pražských názvů.
                </p>

                <p class="font-bold">Ostatní názvy</p>
                <p class="mb-5">
                    Zbývajících 7 % názvů ulic představuje různorodou skupinu.
                    Jsou to nejrůznější názvy podle charakteru (<span
                        class="italic"
                        >Krátká, Příčná, Zahradní, Bílá, Klikatá, Chráněná, V
                        kole, Terasovitá</span
                    >), podle rostlin a hub (nejvíce se jich nachází na
                    Zahradním Městě, ale jejich obliba i v dalších čtvrtích v
                    posledních letech výrazně stoupá , např.
                    <span class="italic"
                        >Devětsilová, Denivková, Hlívová, Chorošová</span
                    >), událostí (<span class="italic"
                        >5. května, 9. května, Revoluce, Osvobození,
                        Prvomájová</span
                    >), nerostů (i prvků či hornin, např.
                    <span class="italic">Onyxová, Jantarová, Křišťálová</span>),
                    zvířat (<span class="italic">Liščí, Sojčí</span>), titulů
                    (<span class="italic">Primátorská, Mendíků</span>), sportů
                    (<span class="italic">Bruslařská, Tenisová</span>) a mnoho
                    dalších.
                </p>

                <p class="mb-10 sm:mb-25">
                    Výše uvedená procenta je však třeba považovat pouze za
                    orientační. Spousta názvů má totiž často mnohoznačný původ,
                    a tak je lze jen těžko zařadit jednoznačně do té či oné
                    skupiny. Tak např. ulice<span class="italic">
                        Foerstrova</span
                    >
                    pojmenovaná podle osoby nese v zásadě i název podle místa,
                    protože skladatel v ulici bydlel.
                    <span class="italic">Mělnická</span> pojmenovaná podle obce
                    nese i název podle bývalého dvora mělnického probošta, který
                    se zde nacházel, má tedy povahu pomístního názvu.
                </p>

                <!-- next chapters -->
                <div class="mb-10 flex justify-between border-t-1 pt-5">
                    <p class="mb-5 w-1/2 border-r-1 font-bold">
                        <nuxt-link
                            to="/clanky/prejmenovavani"
                            class="flex justify-start underline"
                        >
                            <img
                                src="../../public/img/left-arrow.png"
                                alt=""
                                class="order-1 mr-2 inline h-5 w-5"
                            />
                            <span class="order-2 mr-2">Přejmenovávání</span>
                        </nuxt-link>
                    </p>
                    <p class="mb-5 w-1/2 text-right font-bold">
                        <!-- nothing -->
                    </p>
                </div>
            </div>
            <div class="hidden md:col-span-1 md:block" />
        </div>

        <AppFooter />
    </div>
</template>

<script setup lang="ts"></script>

<style scoped></style>
